import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AddAttachmentComponent } from './add-attachment/add-attachment.component';
import { DrawerService } from '../../../../core/services/drawer.service';
import { StudentAttachmentService } from './student-attachment.service';
import { finalize, forkJoin, Observable, of, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import {
  Attachment,
  CaseDetailModel,
  ManageStudentDrawerParams,
  SelectedFolder,
} from '../../insights.interface';
import { AttachmentMode, InsightMode } from '../../insights.enums';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { LoaderService } from '../../../../core/services/loader.service';
import { CommonService } from '../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../core/models/error.models';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFormModule } from 'ng-zorro-antd/form';
import { AttachmentFolderService } from './attachment-folder.service';
import { ApiResponse } from '../../../../core/models/api-response.models';
import { NzIconModule } from 'ng-zorro-antd/icon';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { ManageSettingsService } from '../../../manage-settings/manage-settings.service';
import { QuestionnairesFormComponent } from './questionnaires-form/questionnaires-form.component';
import { QuestionnaireMode } from './questionnaires-form/questionnaires.interface';
import { AuditLogsService } from '../../../audit-logs/audit-logs.service';
import {
  AuditModule,
  AuditOperationType,
} from '../../../audit-logs/audit-logs.constants';
import { AuditContextService } from '../../../audit-logs/audit-context.service';
import { DrawerHeaderComponent } from '../../../../components/drawer-header/drawer-header.component';

interface AttachmentWithSelection {
  id?: number;
  title: string;
  folders: SelectedFolder[];
}

@Component({
  selector: 'app-insights-admin',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    CommonModule,
    NzSpinModule,
    NzEmptyModule,
    ConfirmDialogComponent,
    NzCheckboxModule,
    FormsModule,
    NzCheckboxModule,
    NzInputModule,
    NzIconModule,
    NzFormModule,
    FormsModule,
    NzCollapseModule,
    ReactiveFormsModule,
    DrawerHeaderComponent,
  ],
  templateUrl: './insights-admin.component.html',
  styleUrl: './insights-admin.component.scss',
})
export class InsightsAdminComponent implements OnInit, OnDestroy {
  private readonly studentAttachmentService = inject(StudentAttachmentService);
  private readonly loaderService = inject(LoaderService);
  private readonly auditContextService = inject(AuditContextService);
  private readonly auditLogsService = inject(AuditLogsService);

  generalAttachmentData: AttachmentWithSelection | null = null;
  planData: AttachmentWithSelection[] = [];
  folders: SelectedFolder[] = [];
  hasAttachments = false;

  private readonly destroy$ = new Subject<void>();

  @ViewChild('addAttachmentDrawerTitleTpl')
  addAttachmentDrawerTitleTpl?: TemplateRef<object>;

  @ViewChild('questionnairesDrawerTitleTpl')
  questionnairesDrawerTitleTpl?: TemplateRef<object>;

  loading$: Observable<boolean> = of(false);

  caseParams: ManageStudentDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.VIEW,
  };

  readonly atttachmentMode = AttachmentMode;
  readonly questionnaireMode = QuestionnaireMode;
  showConfirmDialog = false;
  isDeleting = false;
  attachmentId: null | number = null;
  confrimDialogMsg = 'Are you sure you want to delete this attachment?';
  isAddFolderOpen = false;
  selectAll = false; // legacy, remove after migration
  selectAllGeneral = false;
  selectAllPlan: Record<number, boolean> = {};
  folderForm!: FormGroup;
  selectedPanel: boolean[] = [];
  titlePattern = /^\S(.*\S)?$/;
  isSavingFolder = false;
  constructor(
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly folderSrv: AttachmentFolderService,
    private readonly manageSettingsService: ManageSettingsService,
    private readonly fb: FormBuilder
  ) {
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.resetComponentState();
    this.loadPlansWithAttachments();
  }

  loadPlansWithAttachments(): void {
    this.loaderService.show();
    const caseId = this.caseParams.case?.id;
    if (caseId) {
      forkJoin({
        plans: this.manageSettingsService.getAllPlans(
          this?.caseParams?.case?.student?.organizationId ?? 0
        ),
        attachmentsResponse:
          this.studentAttachmentService.getAttachmentsWithPlanTypeId(caseId),
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ plans, attachmentsResponse }) => {
            const planList = this.normalizePlanList(plans.result);
            const allAttachmentGroups = attachmentsResponse.result.data || [];

            this.generalAttachmentData =
              this.buildGeneralAttachmentData(allAttachmentGroups);

            this.planData = this.buildPlanData(planList, allAttachmentGroups);
            this.resetComponentState();
            this.loaderService.hide();
          },

          error: ({ errors, message }: ErrorResponse) => {
            const errorMessages = errors.map(
              err => err.description || err.detailedMessage
            );
            this.commonService.notify('error', errorMessages, message);
            this.resetComponentState();
            this.loaderService.hide();
          },
        });
    }
  }

  private normalizePlanList(
    plansResult: unknown
  ): { id: number; name: string }[] {
    if (Array.isArray(plansResult)) {
      return plansResult as { id: number; name: string }[];
    }
    const items = (plansResult as { items?: { id: number; name: string }[] })
      .items;
    return items || [];
  }

  private buildGeneralAttachmentData(
    attachmentGroups: { planTypeId: number; attachments: Attachment[] }[]
  ): AttachmentWithSelection {
    const generalAttachments = attachmentGroups
      .filter(group => group.planTypeId === 0)
      .flatMap(group => group.attachments.map(att => this.mapAttachment(att)));

    return {
      title: 'General Attachments',
      folders: this.groupByFolder(generalAttachments),
    };
  }

  private buildPlanData(
    plans: { id: number; name: string }[],
    attachmentGroups: { planTypeId: number; attachments: Attachment[] }[]
  ): AttachmentWithSelection[] {
    const availablePlanIds =
      this.getAvailablePlanIdsFromAttachments(attachmentGroups);

    return plans
      .filter(plan => availablePlanIds.has(plan.id))
      .map(plan => ({
        title: plan.name,
        id: plan.id,
        folders: this.getPlanFolders(plan.id, attachmentGroups),
      }));
  }

  private getAvailablePlanIdsFromAttachments(
    attachmentGroups: { planTypeId: number; attachments: Attachment[] }[]
  ): Set<number> {
    return new Set(
      attachmentGroups
        .filter(group => group.planTypeId > 0)
        .map(group => group.planTypeId)
    );
  }

  private getPlanFolders(
    planId: number,
    attachmentGroups: { planTypeId: number; attachments: Attachment[] }[]
  ): SelectedFolder[] {
    const relatedAttachments = attachmentGroups
      .filter(group => group.planTypeId === planId)
      .flatMap(group => group.attachments.map(att => this.mapAttachment(att)));

    return this.groupByFolder(relatedAttachments);
  }

  private mapAttachment(att: Attachment): Attachment {
    return {
      ...att,
      selected: false,
      folderId: att.folderId ?? 1,
      folderName: att.folderName || 'Default',
    } as Attachment;
  }

  groupByFolder(attachments: Attachment[]): SelectedFolder[] {
    const grouped = groupBy(attachments, 'folderId');
    return map(grouped, (atts, folderId) => ({
      folderId: Number(folderId),
      folderName: atts[0].folderName || '',
      selected: false,
      active: true,
      attachments: atts,
    }));
  }

  onFolderExpand(folders: SelectedFolder[], index: number, isActive: boolean) {
    folders[index].active = isActive;
  }

  selectedCount(folders: SelectedFolder[]): number {
    return (
      folders.reduce((count, folder) => {
        const selectedInFolder =
          folder.attachments?.filter((att: Attachment) => att.selected)
            ?.length || 0;
        return count + selectedInFolder;
      }, 0) || 0
    );
  }

  /**
   * Handles select all for general or plan section
   * @param folders
   * @param sectionKey 'general' | planId
   */
  onSelectAllChange(
    folders: SelectedFolder[],
    sectionKey: 'general' | number
  ): void {
    const isGeneral = sectionKey === 'general';
    const selectAllValue = isGeneral
      ? this.selectAllGeneral
      : this.selectAllPlan[sectionKey];
    folders.forEach(folder => {
      folder.selected = selectAllValue;
      folder.attachments.forEach(
        (att: Attachment) => (att.selected = selectAllValue)
      );
    });
  }

  onSelectFolderChange(
    folders: SelectedFolder[],
    folderId: number,
    sectionKey?: 'general' | number
  ): void {
    const folder = folders.find(f => f.folderId === folderId);
    if (!folder) return;

    folder.attachments.forEach(
      att => (att.selected = folder.selected || false)
    );
    this.updateSelectAllState(folders, sectionKey);
  }

  onAttachmentSelectionChange(
    folders: SelectedFolder[],
    sectionKey?: 'general' | number
  ): void {
    folders.forEach(folder => {
      folder.selected = folder.attachments.every(att => att.selected);
    });
    this.updateSelectAllState(folders, sectionKey);
  }

  /**
   * Updates selectAll state for general or plan section
   */
  updateSelectAllState(
    folders: SelectedFolder[],
    sectionKey?: 'general' | number
  ): void {
    const allSelected = folders.every(
      folder => folder.selected && folder.attachments.every(att => att.selected)
    );
    if (sectionKey === 'general') {
      this.selectAllGeneral = allSelected;
    } else if (typeof sectionKey === 'number') {
      this.selectAllPlan[sectionKey] = allSelected;
    } else {
      this.selectAll = allSelected; // fallback for legacy
    }
  }

  private resetSelectionState(): void {
    this.selectAll = false;
    this.selectAllGeneral = false;
    this.selectAllPlan = {};

    this.generalAttachmentData?.folders?.forEach(folder => {
      folder.selected = false;
      folder.attachments.forEach(att => (att.selected = false));
    });

    this.planData?.forEach(plan => {
      if (plan.id != null) {
        this.selectAllPlan[plan.id] = false;
      }
      plan.folders?.forEach(folder => {
        folder.selected = false;
        folder.attachments.forEach(att => (att.selected = false));
      });
    });
  }

  private resetComponentState(): void {
    this.resetSelectionState();
    this.showConfirmDialog = false;
    this.isDeleting = false;
    this.attachmentId = null;
  }

  private logDownloadAction(
    action: 'selected' | 'all',
    item: AttachmentWithSelection
  ): void {
    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const logMessage =
      action === 'selected'
        ? `Downloaded selected attachments for ${studentName}_${item.title}`
        : `Downloaded all attachments for ${studentName}_${item.title}`;

    this.auditLogsService.logAudit({
      auditModule: AuditModule.CaseAttachment,
      auditOperationType: AuditOperationType.Downloaded,
      summary: logMessage,
      organizationIdOverride: this.caseParams.case?.student?.organizationId,
    });
  }

  private downloadAttachments(
    attachments: Attachment[],
    zipFileName: string,
    successMessage: string,
    logAction: () => void
  ): void {
    if (attachments.length === 0) {
      this.commonService.notify(
        'warning',
        'No attachments available to download'
      );
      return;
    }

    this.loaderService.show();
    this.studentAttachmentService
      .downloadAttachmentsAsZip(attachments, zipFileName)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', successMessage);
          logAction();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.logDownloadFailure(zipFileName, errorMessages);
        },
      });
  }

  private logDownloadFailure(
    zipFileName: string,
    errorMessages: string[]
  ): void {
    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const logMessage = `Failed to download attachments (${zipFileName}) for ${studentName}. Errors: ${errorMessages.join(', ')}`;

    this.auditLogsService.logAudit({
      auditModule: AuditModule.CaseAttachment,
      auditOperationType: AuditOperationType.Downloaded,
      summary: logMessage,
      organizationIdOverride: this.caseParams.case?.student?.organizationId,
    });
  }

  downloadSelectedAttachments(item: AttachmentWithSelection): void {
    const selectedAttachments =
      item.folders?.flatMap(folder =>
        folder.attachments.filter((att: Attachment) => att.selected)
      ) || [];

    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const zipFileName = `${studentName}__${item.title}_selected_${Date.now()}.zip`;

    this.downloadAttachments(
      selectedAttachments,
      zipFileName,
      'Selected attachments downloaded successfully',
      () => this.logDownloadAction('selected', item)
    );
  }

  downloadAllAttachments(section: AttachmentWithSelection): void {
    const allAttachments =
      section.folders?.flatMap(folder => folder.attachments) || [];

    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;
    const zipFileName = `${studentName}_${section.title}_all_${Date.now()}.zip`;

    this.downloadAttachments(
      allAttachments,
      zipFileName,
      'All attachments downloaded successfully',
      () => this.logDownloadAction('all', section)
    );
  }

  downloadFile(attachment: Attachment): void {
    const studentName = `${this.caseParams.case?.student?.firstName || 'Unknown'}_${this.caseParams.case?.student?.lastName || 'Student'}`;

    try {
      window.open(attachment.fileUrl, '_blank');
      this.logDownloadSingleAction('success', attachment.name, studentName);
    } catch {
      this.logDownloadSingleAction('failure', attachment.name, studentName);
      this.commonService.notify(
        'error',
        'Failed to download the attachment. Please try again.'
      );
    }
  }

  private logDownloadSingleAction(
    status: 'success' | 'failure',
    fileName: string,
    studentName: string
  ): void {
    const logMessage =
      status === 'success'
        ? `Downloaded single attachment (${fileName}) for ${studentName}`
        : `Failed to download single attachment (${fileName}) for ${studentName}`;

    this.auditLogsService.logAudit({
      auditModule: AuditModule.CaseAttachment,
      auditOperationType: AuditOperationType.Downloaded,
      summary: logMessage,
      organizationIdOverride: this.caseParams.case?.student?.organizationId,
    });
  }

  trackByAttachmentId(index: number, attachment: Attachment): number {
    return attachment.id;
  }

  openAddAttachmentComponentDrawer(
    selectedPlan: AttachmentWithSelection
  ): void {
    this.drawerService
      .openDrawer(
        AddAttachmentComponent,
        this.addAttachmentDrawerTitleTpl || 'Add Attachment',
        520,
        true,
        undefined,
        {
          mode: this.atttachmentMode.CREATE,
          attachment: null,
          planTypeId: selectedPlan.id || 0,
          case: this.caseParams.case,
        },
        DRAWER_IDS.STUDENT_ATTACHMENT
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.planData = [];
        this.loadPlansWithAttachments();
      });
  }

  openAttemptQuestionnaireDrawer(
    selectedPlan: AttachmentWithSelection,
    attachment?: Attachment
  ): void {
    this.drawerService
      .openDrawer(
        QuestionnairesFormComponent,
        this.questionnairesDrawerTitleTpl || 'Attempt Questionnaire',
        1170,
        true,
        undefined,
        {
          mode: this.questionnaireMode.CREATE,
          planTypeId: selectedPlan.id || 0,
          case: this.caseParams.case,
          attachment: attachment,
        },
        DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.planData = [];
        this.loadPlansWithAttachments();
      });
  }

  viewAttachmentDetails(attachment: Attachment, planTypeId?: number): void {
    if (attachment.isQuestionnairePDF) {
      this.drawerService
        .openDrawer(
          QuestionnairesFormComponent,
          this.questionnairesDrawerTitleTpl || 'Attempt Questionnaire',
          1170,
          true,
          undefined,
          {
            mode: this.questionnaireMode.EDIT,
            planTypeId: planTypeId || 0,
            case: this.caseParams.case,
            attachment: attachment,
          },
          DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE
        )
        .afterClose.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.planData = [];
          this.loadPlansWithAttachments();
        });
      return;
    }

    this.drawerService
      .openDrawer(
        AddAttachmentComponent,
        'View Attachment',
        520,
        true,
        undefined,
        {
          attachment,
          mode: this.atttachmentMode.VIEW,
          planTypeId,
          case: this.caseParams.case,
        },
        DRAWER_IDS.STUDENT_ATTACHMENT
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.planData = [];
        this.loadPlansWithAttachments();
      });
  }

  closeAddAttachmentDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.STUDENT_ATTACHMENT);
  }

  closeQuestionnairesDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE);
  }

  getStudentFullName(): string {
    const firstName = this.caseParams.case?.student?.firstName ?? '';
    const lastName = this.caseParams.case?.student?.lastName ?? '';
    return `${firstName} ${lastName}`.trim();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageStudentDrawerParams>(DRAWER_IDS.BIP_STUDENT_ATTACHMENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.folderForm = this.fb.group({
            caseId: this.caseParams?.case?.id,
            name: [
              null,
              [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(this.titlePattern),
              ],
            ],
          });
        }
      });
  }

  onDelete(id: number): void {
    this.showConfirmDialog = true;
    this.attachmentId = id;
  }

  handleDelete(): void {
    if (!this.attachmentId) return;

    this.isDeleting = true;
    this.studentAttachmentService
      .deleteAttachment(this.attachmentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isDeleting = false;
          this.showConfirmDialog = false;
          this.attachmentId = null;
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Attachment deleted successfully'
          );
          this.loadPlansWithAttachments();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.resetComponentState();
        },
      });
  }

  handleCancel(): void {
    this.showConfirmDialog = false;
    this.attachmentId = null;
  }

  onSaveFolder() {
    if (this.folderForm.invalid) {
      this.folderForm.get('name')?.markAsTouched();
      return;
    }

    const modifiedFields = this.auditContextService.buildFieldChanges(
      {},
      this.folderForm.value,
      {
        sensitiveFields: [],
        labelMap: {
          name: 'Folder Name',
        },
        excludeFields: ['caseId'],
      }
    );

    const payload = {
      caseId: this.folderForm.value.caseId,
      name: this.folderForm.value.name,
      _modifiedFields: modifiedFields,
    };
    this.isSavingFolder = true;
    this.folderSrv
      .createFolder(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSavingFolder = false;
        })
      )
      .subscribe({
        next: (res: ApiResponse<boolean>) => {
          if (res.result) {
            this.commonService.notify('success', 'Folder created successfully');
            this.isAddFolderOpen = false;
            this.folderForm.get('name')?.reset();
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages[0] || message);
          this.isSavingFolder = false;
        },
      });
  }

  onCancelFolder() {
    this.isAddFolderOpen = false;
    this.folderForm.get('name')?.reset();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
